import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import { logInfo } from '../../config_and_helpers/logger';
import './AiChatStyle.css';

const LOG_CONTEXT = 'AiChatCitationChip';
const loggedUnknownTypes = new Set();

/**
 * Build a short label for a typed citation chip.
 *
 * @param {Object} citation
 * @returns {string}
 */
export const getCitationLabel = citation => {
  if (!citation) return 'Citation';
  const type = citation.entity_type || 'product';
  if (type === 'product') {
    return (
      citation.cat_num ||
      citation.name ||
      `Product ${citation.product_id ?? '?'}`
    );
  }
  if (type === 'order') {
    return (
      citation.corporate_order_ref ||
      citation.name ||
      `Order ${citation.order_id ?? '?'}`
    );
  }
  if (type === 'quote') {
    return citation.name || `Quote ${citation.quote_id ?? '?'}`;
  }
  if (type === 'supplier') {
    return citation.name || `Supplier ${citation.supplier_id ?? '?'}`;
  }
  if (type === 'project') {
    return citation.name || `Project ${citation.project_id ?? '?'}`;
  }
  return citation.name || type;
};

/**
 * Citation chip linking an assistant reply to a domain entity.
 *
 * @param {Object} props
 * @param {Object} props.citation
 * @returns {JSX.Element|null}
 */
const AiChatCitationChip = ({ citation }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const type = citation?.entity_type;
    if (type && !['product', 'order', 'quote', 'supplier', 'project'].includes(type)) {
      if (!loggedUnknownTypes.has(type)) {
        loggedUnknownTypes.add(type);
        logInfo(LOG_CONTEXT, 'unknown citation entity_type', { entity_type: type });
      }
    }
  }, [citation?.entity_type]);

  if (!citation) return null;

  const entityType = citation.entity_type || 'product';
  const label = getCitationLabel(citation);

  if (entityType === 'product' || entityType === 'order' || entityType === 'quote') {
    return null;
  }

  if (entityType === 'supplier' && citation.supplier_id) {
    return (
      <span className="ai-chat-citation-chip" title={label}>
        <SupplierDetailModal supplierId={citation.supplier_id} />
      </span>
    );
  }

  if (entityType === 'project' && citation.project_id) {
    return (
      <span className="ai-chat-citation-chip" title={label}>
        <Button
          variant="link"
          onClick={() => navigate(`/projects/${citation.project_id}`)}
        >
          {label}
        </Button>
      </span>
    );
  }

  return (
    <span className="ai-chat-citation-chip__static" title={label}>
      {label}
    </span>
  );
};

export default AiChatCitationChip;
